import { useState, useCallback, useEffect } from 'react';
import { UserProfile, UserIntent } from '../types';

interface SignupFlowState {
  step: number;
  profile: Partial<UserProfile>;
  shouldSkipQ2: boolean;
  isComplete: boolean;
}

export const useSignupFlow = (intent: UserIntent) => {
  const [state, setState] = useState<SignupFlowState>({
    step: 1,
    profile: {},
    shouldSkipQ2: intent?.skipOnboarding || false,
    isComplete: intent?.skipOnboarding || false,
  });

  useEffect(() => {
    if (intent?.skipOnboarding) {
      setState(prev => ({
        ...prev,
        shouldSkipQ2: true,
      }));
    }
  }, [intent]);

  const updateProfile = useCallback((updates: Partial<UserProfile>) => {
    setState(prev => ({
      ...prev,
      profile: { ...prev.profile, ...updates },
    }));
  }, []);

  const nextStep = useCallback(() => {
    setState(prev => {
      // If skipOnboarding is true, mark as complete after Q1
      if (intent?.skipOnboarding && prev.step === 1) {
        return {
          ...prev,
          isComplete: true,
        };
      }

      // Skip Q2 if intent detected or user is 'other'
      const shouldSkipQ2 = prev.shouldSkipQ2 || prev.profile.userType == 'unclassified';
      const nextStep = prev.step === 1 && shouldSkipQ2 ? 3 : prev.step + 1;

      return {
        ...prev,
        step: nextStep,
        isComplete: nextStep > 3,
      };
    });
  }, [intent]);

  const prevStep = useCallback(() => {
    setState(prev => ({
      ...prev,
      step: Math.max(1, prev.step - 1),
    }));
  }, []);

  return {
    ...state,
    updateProfile,
    nextStep,
    prevStep,
    shouldSkipQ2: state.shouldSkipQ2 || state.profile.userType == 'unclassified',
  };
};
